import { useEngineBus } from '../hooks/useEngineBus.js';

/**
 * Above this many rounds a pip row stops being countable and becomes a texture,
 * so the bar degrades to ONE continuous fill instead. 12 (the shipped handgun)
 * maps 1:1 onto twelve pips — that is the whole reason `clipSize` is 12, see
 * `src/game/weapons.js` — but a future drum magazine authored at 100 must not
 * render a hundred `<div>`s into a 220 px slab. The threshold is a LEGIBILITY
 * limit, not a performance one: it is the point past which "count what is left"
 * stops being a thing eyes can do, and the numeral has to carry the reading
 * alone.
 */
const MAX_PIPS = 30;

/**
 * @file The ammunition readout — the top slab of the bottom-left cluster, above
 * the health bar and the minimap. Same split as `HealthBar`: PIPS are what you
 * read with peripheral vision mid-fight (twelve of them, one per round, so the
 * count is a glance and not a calculation), the NUMERALS are what you read when
 * you have a second, and the reserve numeral is deliberately dimmer than the
 * clip because the clip is the one that runs out during a burst.
 *
 * ⚠ This component does NOT position itself, for the same reason `HealthBar`
 * and `CashBar` do not: the bottom-left cluster in `routes/PlayRoute.jsx` owns
 * the layout, and ammo/health/minimap share ONE column and ONE width. Three
 * self-positioning slabs drift apart the moment any of them changes size.
 *
 * It returns `null` until the first `'player:ammo'`, so a level with no weapon
 * table (every dev scene, and the whole game before this feature) renders
 * exactly as it did before this file existed. `systems/Weapons.js` publishes
 * ONCE ON CONSTRUCTION — the `Wallet.js` rule — which is what makes the slab
 * appear at the first frame rather than at the first shot.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT DECIDES NOTHING AND KEEPS NO COPY
 * ─────────────────────────────────────────────────────────────────────────────
 * The channel carries the WHOLE state every time, so there is no local clip
 * count here, no reload timer here, and — importantly — **no second opinion on
 * whether a reload is allowed**. The reload zone is always tappable; the model
 * refuses silently when the clip is full, the reserve is empty or a reload is
 * already running (`Weapons#reload` returns false and emits nothing). A
 * `disabled` computed in this file would be the second gate that the
 * `hurtPlayer(amount, lethal)` family of bug is made of: two readings of "may
 * this happen", disagreeing in the one state nobody tests.
 *
 * Everything below is presentation of numbers somebody else owns — a fill
 * fraction, a warning tone, a pip threshold.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ TWO TAP ZONES, NOT ONE WIDGET WITH TWO GESTURES
 * ─────────────────────────────────────────────────────────────────────────────
 * This game targets MOBILE, where there is no `R` and no `X`. The bar/pip area
 * is RELOAD (`'weapon:reload'`); the small labelled pill beside the header is
 * the stance (`'weapon:hot'`). Tap-vs-long-press on ONE target is a mis-tap
 * generator, and on a touch device the labelled pill is the only place the
 * safety catch documents itself at all — a player who has never seen a keyboard
 * hint has no other way to learn the stance exists.
 *
 * Both are real `<button type="button">`s, so they are keyboard- and
 * screen-reader-reachable, and both go through the bus rather than through a
 * method: `Weapons.js` subscribes to those two channels itself, so the touch
 * path and `Gameplay`'s `R`/`X` end in the SAME method and neither can grow its
 * own rules.
 *
 * ⚠ There is NO window-capture key listener in this file, on purpose. `R` and
 * `X` are the engine's (`Gameplay`); this is not a modal, and the project has a
 * live bug on record about capture listeners firing in registration order
 * (`PauseMenu` vs `ShopPanel`).
 *
 * ⚠ Only the two BUTTONS take `pointer-events-auto` — not the whole slab. The
 * HUD wrapper is `pointer-events-none` so that clicks reach the canvas, and the
 * header/numerals/hint rows are not interactive; letting them swallow a drag
 * would cost the player canvas input for nothing. `touchAction: 'manipulation'`
 * kills the 300 ms double-tap-zoom delay, and `onMouseDown` preventDefault is
 * the `Link`/`ShopRow` idiom that stops the canvas losing keyboard focus.
 *
 * ⚠ The root is NOT a `role="status"` live region, unlike `CashBar`. This
 * channel emits ~16 times per reload; a live region would read the whole slab
 * out on every one of them. The counts ride the buttons' `aria-label`s instead.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 * @param {number} [props.width=220] CSS px — match the minimap.
 */
export default function AmmoBar({ engine, width = 220 }) {
  const ammo = useEngineBus(engine, 'player:ammo', { throttle: 100 });
  // 100 ms matches `HealthBar` and is comfortably faster than both things this
  // channel reports: the 0.28 s fire cadence and `Weapons.js`'s 0.1 s reload
  // sub-tick. It coalesces, it never lags.
  if (!ammo) return null;

  /** The bus is the only thing this widget can push on. Absent = inert taps. */
  const bus = engine?.bus ?? null;
  const tap = (channel) => () => bus?.emit?.(channel);

  // Every read is guarded the way `Weapons.js` guards its own def fields: a
  // malformed payload must render a wrong-but-whole HUD, never a NaN.
  const clipSize = Math.max(0, Math.floor(Number(ammo.clipSize) || 0));
  const clip = Math.max(0, Math.min(clipSize, Math.floor(Number(ammo.clip) || 0)));
  const reserve = Math.max(0, Math.floor(Number(ammo.reserve) || 0));
  const reloadTime = Math.max(0, Number(ammo.reloadTime) || 0);
  const reloadT = Math.max(0, Math.min(reloadTime, Number(ammo.reloadT) || 0));
  const reloading = !!ammo.reloading;
  // `hot` is a boolean on the wire; `=== false` and not `!hot` so a payload
  // missing the field reads as ARMED rather than as a permanent safety catch.
  const safe = ammo.hot === false;
  // The one unrecoverable state: nothing in the gun, nothing in the pocket, and
  // no clock running that could change that. `Weapons.js` calls it terminal —
  // the way out is a purchase or a respawn, both of which are events elsewhere.
  const dry = !reloading && clip === 0 && reserve === 0;

  /** Reload fill, 0 → 1. Derived from the two clocks; never accumulated here. */
  const progress = reloadTime > 0 ? (reloadTime - reloadT) / reloadTime : 1;
  /** 0 = draw one continuous bar instead (see {@link MAX_PIPS}). */
  const pips = clipSize > 0 && clipSize <= MAX_PIPS ? clipSize : 0;

  // A warning tone, not a second reading: the pips still say the same number,
  // they just say it louder over the last quarter of the clip.
  const low = clip > 0 && clip <= Math.max(1, Math.ceil(clipSize * 0.25));
  const pipOn = dry ? '#ff2e4d' : low ? '#ffc10a' : '#d7dcea';
  const pipOff = '#191d26';

  // The header word, ranked hardest-state-first. DRY outranks everything
  // because it is the only state the player cannot play out of; SAFE still
  // reads off the pill below even when a reload is covering this slot.
  const status = dry
    ? 'No ammo'
    : reloading
      ? 'Reloading'
      : safe
        ? 'Safe'
        : typeof ammo.label === 'string' && ammo.label
          ? ammo.label
          : 'Ammo';
  const statusTone = dry || safe ? '#ff2e4d' : reloading ? '#ffc10a' : '#8b93a7';

  return (
    <div className="select-none" style={{ width: `${width}px` }}>
      <div className="flex items-end justify-between gap-2 px-0.5 pb-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span
            className="font-hud truncate text-[11px] font-bold uppercase leading-none tracking-[0.22em]"
            style={{ color: statusTone }}
          >
            {status}
          </span>

          {/* THE STANCE PILL — zone two. Labelled with the state it is IN
              (`SAFE` when the gun is down), because that is the reading a
              player needs; a button labelled with its action would say "go
              hot" and leave the current state to be inferred. */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={tap('weapon:hot')}
            aria-label={safe ? 'Weapon on safe — tap to go hot' : 'Weapon hot — tap to go safe'}
            style={{ touchAction: 'manipulation' }}
            className={`pointer-events-auto rounded-[2px] border px-1.5 py-[1px] font-hud text-[9px] font-bold uppercase leading-[1.35] tracking-[0.18em] transition ${
              safe
                ? 'border-siren bg-siren/15 text-siren'
                : 'border-hairline bg-void/80 text-hud-dim hover:border-gold hover:text-gold'
            }`}
          >
            {safe ? 'Safe' : 'Hot'}
          </button>
        </div>

        <div className="flex shrink-0 items-baseline gap-[3px]">
          <span
            className="gta-outline font-display text-[19px] leading-none tabular-nums"
            style={{ color: dry ? '#ff2e4d' : low ? '#ffc10a' : '#e6e9f2' }}
          >
            {/* `--` while reloading: the clip really is indeterminate until the
                far edge lands, and printing 0 would read as a dry gun. */}
            {reloading ? '--' : clip}
          </span>
          <span className="gta-outline font-display text-[12px] leading-none text-hud-faint">/</span>
          <span className="gta-outline font-display text-[13px] leading-none tabular-nums text-hud-dim">
            {reserve}
          </span>
        </div>
      </div>

      {/* THE RELOAD ZONE — zone one, and the big one, because a mid-fight tap
          is not an aimed tap. It is always enabled: the model owns whether a
          reload may start (see the header). */}
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={tap('weapon:reload')}
        aria-label={`Reload — ${clip} of ${clipSize} in the clip, ${reserve} in reserve`}
        style={{
          touchAction: 'manipulation',
          // SAFE is legible WITHOUT motion: the whole bar drains of colour and
          // sits there drained. A blink would say "look at me now"; a forgotten
          // safety catch needs to say "this is why nothing is happening" thirty
          // seconds later. The pill above keeps its full colour — it is outside
          // this filter on purpose.
          filter: safe ? 'saturate(0.12) brightness(0.72)' : 'none',
        }}
        className="pointer-events-auto flex w-full gap-[2px] rounded-[3px] border border-hairline bg-void/80 p-[3px] transition hover:border-gold/60"
      >
        {reloading ? (
          // ONE amber bar for the whole clip. It replaces the pips rather than
          // animating them, because a row of pips filling left-to-right and a
          // row of pips EMPTYING left-to-right are the same picture.
          <div className="h-[9px] w-full overflow-hidden rounded-[1px]" style={{ backgroundColor: pipOff }}>
            <div
              className="h-full rounded-[1px]"
              style={{
                width: `${Math.max(0, Math.min(1, progress)) * 100}%`,
                backgroundColor: '#ffc10a',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.32)',
              }}
            />
          </div>
        ) : pips > 0 ? (
          // Pips empty RIGHT-TO-LEFT: the lit run is `i < clip`, so the round
          // you are about to spend is always the rightmost lit one.
          Array.from({ length: pips }, (_, i) => (
            <div
              key={i}
              className={`h-[9px] flex-1 rounded-[1px] ${dry ? 'wanted-pulse' : ''}`}
              style={{
                backgroundColor: i < clip ? pipOn : pipOff,
                boxShadow: i < clip ? 'inset 0 1px 0 rgba(255,255,255,0.28)' : 'none',
              }}
            />
          ))
        ) : (
          // Degraded: a clip too big to count (or no weapon resolved at all).
          <div className={`h-[9px] w-full overflow-hidden rounded-[1px] ${dry ? 'wanted-pulse' : ''}`} style={{ backgroundColor: pipOff }}>
            <div
              className="h-full rounded-[1px]"
              style={{
                width: `${clipSize > 0 ? (clip / clipSize) * 100 : 0}%`,
                backgroundColor: pipOn,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28)',
              }}
            />
          </div>
        )}
      </button>

      {/* THE KEYS, PRINTED. The widget names the real bindings so the key and
          the pixel cannot drift apart — `X` prints what it will DO next, which
          is the opposite of the pill above it and correct for both: a pill
          states, a key hint instructs. */}
      <div className="flex items-baseline justify-between gap-2 px-0.5 pt-1 font-hud text-[9px] uppercase leading-none tracking-[0.18em] text-hud-faint">
        <span>R — Reload</span>
        <span>X — {safe ? 'Go hot' : 'Safe'}</span>
      </div>

      {dry ? (
        // The only unrecoverable state gets the only extra row in the widget:
        // there is no trigger the player can pull to fix this and no clock that
        // will fix it for them, so the readout has to say where the fix IS.
        <div className="px-0.5 pt-1 font-hud text-[10px] font-bold uppercase leading-none tracking-[0.18em] text-siren">
          Buy ammo at a gunsmith
        </div>
      ) : null}
    </div>
  );
}
